/**
 * `preencher` — põe valores num formulário ABERTO na tela, e nunca salva
 * ([T015] / US-4; canal `GlobalBus` no [T019] / US-5 da spec-010).
 *
 * Espelho do `ToolPreencher` do backend, cuja descrição promete ao modelo:
 * "★ NÃO salva: quem grava é o clique do usuário no botão do próprio
 * formulário". É por isso que o único efeito daqui é UM dispatch (canal
 * `store`) ou UM `$emit` (canal `globalbus`) — e é por isso que o teste conta
 * os dois e assere que nenhum POST saiu.
 *
 * ## O registro de formulários é a fronteira
 *
 * Formulário não registrado é RECUSADO com a lista dos que existem. Aceitar
 * qualquer nome faria o modelo inventar telas e o usuário ver "preenchi" sobre
 * um formulário que nunca existiu.
 *
 * ★ **Quem registra é o DONO do formulário, não este arquivo** ([T019]).
 *   Até a spec-001 o mapa era literal aqui dentro e importava
 *   `@modules/fee-fixo/utils/filtro-tipado` — o achado I-5 da review: a tool
 *   genérica passava a depender de um módulo de produto, e cada formulário novo
 *   somaria um import de outro módulo neste mesmo arquivo. Agora cada dono
 *   chama `registrarFormulario` no BOOT (o fee-fixo em `modules/fee-fixo/index.js`,
 *   como já faz com o seu registry parcial de cartões; os dois formulários de
 *   modal em `./formularios-globalbus`, carregado por `./index.js`). Aqui ficam
 *   só os DOIS canais e as regras que valem para qualquer formulário.
 *
 * ## Os dois canais
 *
 * | canal | como aplica | quem usa |
 * |---|---|---|
 * | `store` | `ctx.store.dispatch(namespace + '/' + action, payload)` | a tela já montada lê o valor de um getter (recorte de clientes do Fee Fixo) |
 * | `globalbus` | `GlobalBus.$emit(evento, payload)` | o formulário é um MODAL, que abre já preenchido (nova oportunidade, nova tarefa) |
 *
 * O canal `globalbus` tem duas guardas que o `store` não tem, e as duas são
 * sobre não destruir trabalho do usuário — ver `conferirCanalDoGlobalBus`.
 */
import { GlobalBus } from '@services/bus'

export const NOME_DA_TOOL = 'preencher'
export const LABEL = 'Preencher formulário'

/** Aplica por `store.dispatch`: a tela alvo já está montada e lê de um getter. */
export const CANAL_STORE = 'store'
/** Aplica por `GlobalBus.$emit`: o formulário é um modal, que abre preenchido. */
export const CANAL_GLOBALBUS = 'globalbus'

const CANAIS = [CANAL_STORE, CANAL_GLOBALBUS]

/**
 * O registro vivo. Chave = nome do formulário (`<modulo>/<formulario>`), no
 * mesmo formato das chaves do `UI_REGISTRY`.
 */
export const FORMULARIOS = {}

/**
 * Registra um formulário que a IA sabe preencher.
 *
 * @param {Object} entrada
 * @param {string} entrada.nome        `<modulo>/<formulario>`.
 * @param {string} entrada.canal       `CANAL_STORE` ou `CANAL_GLOBALBUS`.
 * @param {string} [entrada.namespace] Canal `store`: namespace Vuex.
 * @param {string} [entrada.action]    Canal `store`: action que recebe o payload.
 * @param {string} [entrada.evento]    Canal `globalbus`: nome do evento (`MODAL.*`).
 * @param {Function} entrada.validar   `(campos) => {valido, erros, ...}` — o
 *   validador DA PRÓPRIA TELA. Escrever uma segunda régua aqui é o que o
 *   [G-Q2] já custou uma vez.
 * @param {Function} entrada.montarPayload `(veredito) => payload` que vai no fio.
 * @param {Function} entrada.camposAplicados `(veredito) => string[]`.
 * @returns {Function} desfaz o registro (útil no teste).
 */
export function registrarFormulario(entrada) {
  const problema = conferirEntrada(entrada)
  if (problema) throw new Error('[preencher] ' + problema)

  // Chave repetida ESTOURA, como no `registrar` do `ui-registry`: dois donos
  // disputando o mesmo nome se sobrescreveriam em silêncio, e o formulário que
  // o usuário veria dependeria da ordem do boot.
  if (formularioRegistrado(entrada.nome)) {
    throw new Error(
      '[preencher] o formulário "' + entrada.nome + '" já está registrado.'
    )
  }

  FORMULARIOS[entrada.nome] = entrada
  return () => esquecerFormulario(entrada.nome)
}

function conferirEntrada(entrada) {
  if (!entrada || !entrada.nome) return 'formulário sem nome.'
  if (CANAIS.indexOf(entrada.canal) === -1) {
    return 'o formulário "' + entrada.nome + '" declarou o canal "' +
      entrada.canal + '", que não existe.'
  }
  if (typeof entrada.validar !== 'function') {
    return 'o formulário "' + entrada.nome + '" não trouxe validador.'
  }
  if (entrada.canal === CANAL_STORE && (!entrada.namespace || !entrada.action)) {
    return 'o formulário "' + entrada.nome + '" usa o canal da store sem ' +
      'namespace/action.'
  }
  if (entrada.canal === CANAL_GLOBALBUS && !entrada.evento) {
    return 'o formulário "' + entrada.nome + '" usa o canal do GlobalBus sem evento.'
  }
  return null
}

/** Já existe formulário com este nome? Os registradores de boot consultam. */
export function formularioRegistrado(nome) {
  return Object.prototype.hasOwnProperty.call(FORMULARIOS, nome)
}

export function esquecerFormulario(nome) {
  delete FORMULARIOS[nome]
}

/** Esvazia o registro. Existe para os testes. */
export function limparFormularios() {
  Object.keys(FORMULARIOS).forEach((nome) => {
    delete FORMULARIOS[nome]
  })
}

/** Os formulários que este front sabe preencher — entra na mensagem de recusa. */
export function nomesDosFormularios() {
  return Object.keys(FORMULARIOS).sort()
}

const recusar = (motivo) => ({ ok: false, motivo: motivo })

export const MOTIVO_SEM_CONTEXTO =
  'a tela que você está usando não registrou o contexto de execução, então não ' +
  'é possível preencher formulário por aqui.'

/**
 * O trecho literal que o teste (e a spec, US-5) cobram na recusa por modal
 * aberto. Constante para que reescrever a frase não desligue a guarda em
 * silêncio.
 */
export const MOTIVO_FORMULARIO_ABERTO = 'há um formulário aberto'

/**
 * Tem um modal aberto na tela AGORA?
 *
 * O que foi MEDIDO (26/08): nenhum dos dois modais alvo expõe "aberto" — nem
 * em flag global, nem por evento no `GlobalBus`. O `BaseModal` guarda `dialog`
 * no próprio `data` e o abre por `busModal.$emit('dialog', true)`, um bus
 * privado da instância. O que EXISTE e é público é a marca que o Vuetify põe no
 * DOM (`.v-dialog__content--active`), já lida com esse mesmo propósito em
 * `atendimento-popup.vue:275`.
 *
 * A leitura é deliberadamente ABRANGENTE (qualquer diálogo aberto, não só o
 * alvo): reabrir um modal chama o `open()` dele, que começa por `clear()` e
 * apaga o que o usuário digitou (`crm-opportunities-modal.vue:106`,
 * `task-detail-modal.vue:157`). Entre recusar demais e apagar o formulário que
 * o usuário estava preenchendo, a recusa é o erro barato — e o motivo diz ao
 * modelo exatamente o que pedir ao usuário.
 */
export function formularioJaAberto() {
  if (typeof document === 'undefined') return false
  return document.querySelectorAll('.v-dialog__content--active').length > 0
}

/**
 * Alguém está ouvindo este evento no `GlobalBus`?
 *
 * `$emit` sem ouvinte é um no-op silencioso — e o desfecho seria o pior
 * possível: `preenchido: true` sobre um formulário que nunca abriu. Os dois
 * modais alvo são montados em `components/global-modals.vue` (dentro do layout
 * `main`), então o ouvinte existe em qualquer rota autenticada; quando não
 * existir, é porque a tela não está lá.
 *
 * Fail-OPEN quando `_events` não existe: se o Vue mudar de internals, a tool
 * volta a tentar em vez de recusar tudo. Só a AUSÊNCIA da chave dentro de um
 * `_events` presente é medição de "ninguém ouve".
 */
export function temOuvinteNoBus(evento) {
  const eventos = GlobalBus && GlobalBus._events
  if (!eventos) return true
  const ouvintes = eventos[evento]
  return Boolean(ouvintes && ouvintes.length)
}

/** O formulário existe e veio campo para pôr nele? */
function conferirPedido(dados) {
  const nome = dados.formulario
  const entrada = nome ? FORMULARIOS[nome] : null
  if (!entrada) {
    return recusar(
      'o formulário "' + nome + '" não existe nesta tela. Formulários que sei ' +
        'preencher: ' + nomesDosFormularios().join(', ') + '.'
    )
  }
  const campos = dados.campos
  if (!campos || typeof campos !== 'object' || !Object.keys(campos).length) {
    return recusar(
      'nenhum campo informado para preencher o formulário "' + nome + '".'
    )
  }
  return { ok: true, entrada: entrada }
}

const temStore = (ctx) =>
  Boolean(ctx && ctx.store && typeof ctx.store.dispatch === 'function')

function conferirCanalDoGlobalBus(entrada) {
  if (formularioJaAberto()) {
    return recusar(
      MOTIVO_FORMULARIO_ABERTO + ' na tela. Não vou sobrescrever o que o ' +
        'usuário está preenchendo: peça para ele concluir ou fechar esse ' +
        'formulário antes.'
    )
  }
  if (!temOuvinteNoBus(entrada.evento)) {
    return recusar(
      'o formulário "' + entrada.nome + '" não está disponível nesta tela. ' +
        'Leve o usuário até a tela dele antes, num turno anterior.'
    )
  }
  return null
}

/** O canal do formulário consegue receber AGORA? */
function conferirCanal(entrada, ctx) {
  if (entrada.canal === CANAL_STORE) {
    return temStore(ctx) ? null : recusar(MOTIVO_SEM_CONTEXTO)
  }
  return conferirCanalDoGlobalBus(entrada)
}

/**
 * @param {Object} input `{formulario, campos}`.
 * @returns {{ok: boolean, motivo?: string, entrada?: Object, veredito?: Object, payload?: Object}}
 */
export function guard(input, ctx) {
  const dados = input || {}

  const pedido = conferirPedido(dados)
  if (!pedido.ok) return pedido
  const entrada = pedido.entrada
  const nome = dados.formulario

  // O ambiente ANTES dos campos: com um formulário aberto na tela, corrigir os
  // campos não muda nada — o modelo precisa parar, não tentar de novo.
  const canalIndisponivel = conferirCanal(entrada, ctx)
  if (canalIndisponivel) return canalIndisponivel

  const veredito = entrada.validar(dados.campos)
  if (!veredito || !veredito.valido) {
    const erros = (veredito && veredito.erros) || []
    return recusar(
      'os campos não passaram na validação do formulário "' + nome + '": ' +
        erros.join(' ')
    )
  }

  return {
    ok: true,
    entrada: entrada,
    veredito: veredito,
    payload: entrada.montarPayload(veredito),
  }
}

function aplicar(entrada, payload, ctx) {
  if (entrada.canal === CANAL_STORE) {
    return ctx.store.dispatch(entrada.namespace + '/' + entrada.action, payload)
  }
  return GlobalBus.$emit(entrada.evento, payload)
}

/**
 * @returns {Promise<{preenchido: boolean, campos_aplicados: string[], motivo?: string}>}
 */
export function executar(input, ctx) {
  const veredito = guard(input, ctx)
  if (!veredito.ok) {
    return Promise.resolve({
      preenchido: false,
      campos_aplicados: [],
      motivo: veredito.motivo,
      recusado: true,
    })
  }

  const entrada = veredito.entrada
  return Promise.resolve()
    .then(() => aplicar(entrada, veredito.payload, ctx))
    .then(() => ({
      preenchido: true,
      campos_aplicados: entrada.camposAplicados(veredito.veredito),
    }))
}

export default { nome: NOME_DA_TOOL, label: LABEL, guard: guard, executar: executar }
